import React, { useState, useEffect } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip, Breadcrumbs, Typography, Link, Badge } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../AuthContext';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { useLocation, useNavigate } from 'react-router-dom';
import configData from '../configure';
import './Topbar.css';

const Topbar = ({ userOrgId, currentOrgId, onOrgIdSelect, currentClass, onMenuClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, axios: authAxios, userRole, user, token, unreadCount, refreshUnreadCount } = useAuth();
  const [orgName, setOrgName] = useState('');

  // Fetch organization name when currentOrgId changes
  useEffect(() => {
    const fetchOrgName = async () => {
      if (currentOrgId && currentOrgId !== 'undefined' && authAxios) {
        try {
          const response = await authAxios.get(
            configData.get_identity_identity_org_orgid(currentOrgId, currentOrgId),
            { headers: { accept: 'application/hal+json' } }
          );
          if (response.data && (response.data.orgName || response.data.name)) {
            setOrgName(response.data.orgName || response.data.name);
          }
        } catch (error) {
          console.error("Error fetching organization name:", error);
        }
      }
    };
    fetchOrgName();
  }, [currentOrgId, authAxios]);

  const goToSettingsPage = () => {
    navigate('/settings');
  };

  // State and handlers for the profile menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Generate breadcrumbs from pathname
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    const pathTitleMap = {
      'classes': 'Classes',
      'subjects': 'Subjects',
      'students': 'Students',
      'teachers': 'Teachers',
      'courses': 'Courses',
      'timetable': 'Timetable',
      'settings': 'Settings',
      'analytics': 'Firebase Analytics',
      'profile': 'Profile'
    };

    const breadcrumbs = [
      {
        label: 'Home',
        path: '/',
        icon: <HomeIcon sx={{ fontSize: 20, mr: 0.5 }} />
      }
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Check if this is the classes path and we have currentClass info
      if (segment === 'classes' && index === pathSegments.length - 1) {
        breadcrumbs.push({
          label: 'Classes',
          path: currentPath,
          isLast: false
        });

        // Add the current class name if available
        if (currentClass) {
          breadcrumbs.push({
            label: `${currentClass.className} ${currentClass.sectionName}`,
            path: currentPath,
            isLast: true
          });
        }
      } else {
        const label = pathTitleMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);

        breadcrumbs.push({
          label,
          path: currentPath,
          isLast: index === pathSegments.length - 1 && !currentClass
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
      <Box display="flex" alignItems="center">
        <IconButton
          size="small"
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { xs: 'inline-flex', md: 'none' }, color: '#6B7280' }}
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: '#6B7280' }} />}
          aria-label="breadcrumb"
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = crumb.isLast || index === breadcrumbs.length - 1;

            if (isLast) {
              return (
                <Typography
                  key={`${crumb.path}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#1976d2',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  {crumb.icon}
                  {crumb.label}
                </Typography>
              );
            }

            return (
              <Link
                key={`${crumb.path}-${index}`}
                underline="hover"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#6B7280',
                  cursor: 'pointer',
                  fontSize: '14px',
                  '&:hover': {
                    color: '#1976d2'
                  }
                }}
                onClick={() => navigate(crumb.path)}
              >
                {crumb.icon}
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>

      <Box display="flex" alignItems="center">
        {orgName && (
          <Tooltip title={orgName}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                color: '#1976d2',
                padding: '4px 12px',
                borderRadius: '20px',
                ml: 1,
                mr: 1,
                maxWidth: { xs: '120px', sm: '200px', md: '300px' },
                border: '1px solid rgba(25, 118, 210, 0.2)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }
              }}
            >
              <CorporateFareIcon sx={{ fontSize: 18, mr: 1, flexShrink: 0 }} />
              <Typography
                variant="subtitle2"
                noWrap
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.02em',
                }}
              >
                {orgName}
              </Typography>
            </Box>
          </Tooltip>
        )}

        <Tooltip title="Notifications">
          <IconButton 
            size="small" 
            aria-label="Open notifications" 
            onClick={() => navigate(userRole === 'STUDENT' ? '/student-inbox' : '/erp-inbox')}
          >
            <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '10px', height: '16px', minWidth: '16px' } }}>
              <NotificationsOutlinedIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title="Settings">
          <IconButton size="small" onClick={goToSettingsPage} aria-label="Open settings">
            <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Profile">
          <IconButton size="small" onClick={handleMenuOpen} aria-label="Open profile menu">
            <PersonOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
          <MenuItem onClick={logout}>
            <LogoutIcon sx={{ mr: 1, fontSize: 20 }} /> Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>

  );
};

export default Topbar;
